'use client';

import { create } from 'zustand';

/** Display data preferences (FIX03: real controls on /profile?tab=data). */
export const TIMEZONES = [
  { value: 'Asia/Shanghai', label: '上海', labelEn: 'Shanghai' },
  { value: 'Asia/Hong_Kong', label: '香港', labelEn: 'Hong Kong' },
  { value: 'Asia/Taipei', label: '台北', labelEn: 'Taipei' },
  { value: 'Asia/Tokyo', label: '东京', labelEn: 'Tokyo' },
  { value: 'Asia/Singapore', label: '新加坡', labelEn: 'Singapore' },
  { value: 'America/New_York', label: '纽约', labelEn: 'New York' },
  { value: 'America/Los_Angeles', label: '洛杉矶', labelEn: 'Los Angeles' },
  { value: 'Europe/London', label: '伦敦', labelEn: 'London' },
  { value: 'UTC', label: 'UTC', labelEn: 'UTC' },
] as const;

export const REFRESH_INTERVALS = [3, 5, 10, 30] as const;

/** AUD11: the 15 K-line timeframes supported by the chart (mirrors KLINE_INTERVALS). */
export const DEFAULT_INTERVALS = [
  '1m', '3m', '5m', '15m', '30m', '1h', '2h', '4h', '6h', '8h', '12h', '1d', '3d', '1w', '1M',
] as const;

export const DEFAULT_FIB_KEY = 'glint-fib';

interface DataSettingsState {
  timezone: string;
  refreshInterval: number;
  setTimezone: (tz: string) => void;
  setRefreshInterval: (sec: number) => void;
}

const read = (k: string, fallback: string) =>
  typeof window !== 'undefined' ? localStorage.getItem(k) || fallback : fallback;

export const useDataSettings = create<DataSettingsState>((set) => ({
  timezone: read('ailumix-tz', 'Asia/Shanghai'),
  refreshInterval: Number(read('ailumix-refresh', '5')) || 5,
  setTimezone: (timezone) => {
    localStorage.setItem('ailumix-tz', timezone);
    set({ timezone });
  },
  setRefreshInterval: (refreshInterval) => {
    localStorage.setItem('ailumix-refresh', String(refreshInterval));
    set({ refreshInterval });
  },
}));

/** IANA timezone label, e.g. "Asia/Shanghai (UTC+8)". */
export function timezoneLabel(tz: string, zh: boolean): string {
  try {
    const parts = new Intl.DateTimeFormat('en-US', { timeZone: tz, timeZoneName: 'shortOffset' }).formatToParts(new Date());
    const offset = parts.find((p) => p.type === 'timeZoneName')?.value || '';
    return `${tz} (${offset || 'UTC'})`;
  } catch {
    return tz;
  }
}

/**
 * 按用户选择时区格式化时间（默认 Asia/Shanghai）。
 * 修复 SSR toLocaleString 落在 UTC 的坑：显式传 timeZone，非法输入返回 '—'。
 * compact=true 时输出「M/D HH:mm」短格式（详情页/信号摘要），否则含年份完整格式。
 */
export function formatTimeInTz(
  v: number | string | Date | null | undefined,
  tz: string,
  zh: boolean,
  compact = false,
): string {
  if (v === null || v === undefined || v === '') return '—';
  const d = new Date(typeof v === 'number' ? v : String(v));
  if (Number.isNaN(d.getTime())) return '—';
  const locale = zh ? 'zh-CN' : 'en-US';
  const base: Intl.DateTimeFormatOptions = {
    timeZone: tz,
    hour12: false,
    month: compact ? 'numeric' : '2-digit',
    day: compact ? 'numeric' : '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  };
  if (!compact) base.year = 'numeric';
  try {
    return new Intl.DateTimeFormat(locale, base).format(d);
  } catch {
    return d.toLocaleString(locale, { hour12: false, year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' });
  }
}
